import React, { useCallback } from "react";
import { StyleSheet, View } from "react-native";
import { useFocusEffect, useNavigation, useRoute } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { useAuth } from "../hooks/useAuth";
import type { RootStackParamList } from "../navigation/navigation.types";
import WebSocketNavigator from "../navigation/WebSocketNavigator";
import { useChatBar, ConversationType } from "../contexts/ChatBarContext";
import returnToUtils from "../utils/returnTo.utils";
import { evaluateAuthGuard, UserRoleUI } from "@axio/auth-guard";
import { useAppConfig } from "../AppConfigProvider";
import Loader from "../components/common/status/Loader/Loader";

type WithAuthOptions = {
  minRequiredRole?: UserRoleUI;
  redirectToIfNoPermission?: keyof RootStackParamList;
};

const withAuth = (WrappedComponent, options: WithAuthOptions = {}) => {
  const WithAuthComponent = (props) => {
    const { isCompanionMode, isLTI, ltiLaunchUrl } = useAppConfig();
    const {
      isAuthenticated,
      isEmailVerified,
      otpVerifiedForSession,
      userInfo,
      meData,
      isAuthReady,
      isPermissionsReady,
      isUpdating,
    } = useAuth();
    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
    const route = useRoute();
    const name = route.name;
    const params = route.params;
    const { mapRouteToContext, setActiveContextAndObjectId, setContext, setConversationType } = useChatBar();
    const { minRequiredRole, redirectToIfNoPermission } = options;

    const isVerified = isEmailVerified && otpVerifiedForSession;

    WebSocketNavigator();

    useFocusEffect(
      useCallback(() => {
        const activeContext = mapRouteToContext(name as keyof RootStackParamList, params);

        let objectId = null;
        switch (activeContext) {
          case "track":
            objectId = (params as RootStackParamList["Lesson Chat"]).loId;
            break;
          case "assignment":
          case "ai_tutor":
            objectId = (params as RootStackParamList["Resource"]).resource_id;
            break;
          case "onboarding":
          case "general":
          default:
            objectId = null;
            break;
        }

        let contentType = null;
        switch (activeContext) {
          case "track":
            contentType = ConversationType.LEARNING;
            break;
          case "assignment":
          case "ai_tutor":
            contentType = ConversationType.SCHOOL;
            break;
          case "onboarding":
            contentType = ConversationType.ONBOARD;
            break;
          case "general":
          default:
            contentType = ConversationType.GENERAL_CONVERSATION;
            break;
        }

        setConversationType(contentType);
        setActiveContextAndObjectId({ context: activeContext, objectId });
        setContext({ context: activeContext, params: params as { [key: string]: string } });
      }, [mapRouteToContext, name, params, setActiveContextAndObjectId, setContext, setConversationType])
    );

    useFocusEffect(
      useCallback(() => {
        const decision = evaluateAuthGuard(
          {
            isAuthReady,
            isPermissionsReady,
            isAuthenticated,
            isEmailVerified,
            otpVerifiedForSession,
            isUpdating,
            userRole: userInfo.userRole ?? UserRoleUI.STUDENT,
          },
          {
            isCompanionMode,
            isOnboardingRoute: name === "Onboarding",
            minRequiredRole,
            redirectToIfNoPermission: redirectToIfNoPermission as string | undefined,
            isLtiFlow: isLTI,
            ltiLaunchUrl,
          }
        );

        if (decision.status !== "redirect") {
          return;
        }

        const destination = decision.destination;
        const currentUrl = window.location.pathname + window.location.search;

        if (destination.kind === "external") {
          if (destination.url) {
            window.location.href = destination.url;
          }
          return;
        }

        if (destination.kind === "login") {
          returnToUtils.storeReturnToUrl(currentUrl);
          navigation.reset({
            index: 0,
            routes: [{ name: "Login" }],
          });
          return;
        }

        if (destination.kind === "verify-email") {
          returnToUtils.storeReturnToUrl(currentUrl);
          navigation.reset({
            index: 0,
            routes: [{ name: "Verify Email" }],
          });
          return;
        }

        const routeName = destination.route as keyof RootStackParamList;
        navigation.reset({
          index: 0,
          routes: [{ name: routeName, params }],
        });
      }, [
        isAuthReady,
        isPermissionsReady,
        isAuthenticated,
        isEmailVerified,
        otpVerifiedForSession,
        isUpdating,
        userInfo.userRole,
        isCompanionMode,
        name,
        minRequiredRole,
        redirectToIfNoPermission,
        isLTI,
        ltiLaunchUrl,
        navigation,
        params,
      ])
    );

    if (!isAuthReady || isUpdating) {
      return (
        <View style={styles.loaderContainer}>
          <Loader />
        </View>
      );
    }

    if (!isAuthenticated || !meData?.me || !isVerified) {
      return (
        <View style={styles.loaderContainer}>
          <Loader />
        </View>
      );
    }

    return <WrappedComponent {...props} />;
  };

  return WithAuthComponent;
};

const styles = StyleSheet.create({
  loaderContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
});

export default withAuth;
